/**
 * Card for a VSC report.
 */
import * as React from 'react';
import { Calendar, UserCheck } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import type { VscReportPageActions } from './vsc-report-configs';
import {
  deriveVscReportDisplay,
  getVscReportActionState,
  resolveVscReportPermissions,
} from './vsc-report-table-utils';
import type { VscReport } from '@/types/vsc-report.types';

import { DataTableRowCheckbox } from '@/components/common/data-table/DataTableRowCheckbox';

/** Props for {@link VscReportCard}. */
interface VscReportCardProps {
  vscReport: VscReport;
  onView?: (vscReportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
  pageActions?: VscReportPageActions;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

/** Card for a VSC report. */
export const VscReportCard: React.FC<VscReportCardProps> = ({
  vscReport,
  onView,
  onEdit,
  onDelete,
  onValidate,
  pageActions,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const { canEdit: canEditResolved, canValidate } = resolveVscReportPermissions(pageActions);
  const canEdit = getRowPermission(vscReport, 'action-edit-row', canEditResolved);
  const canDelete = getRowPermission(
    vscReport,
    'action-delete-row',
    pageActions?.canDeleteRow ?? true,
  );

  const { statusConfig, StatusIcon, volunteerName, sessionDisplayText, establishmentName } =
    deriveVscReportDisplay(vscReport);
  const { editDisabled, editTooltip, validateDisabled, validateTooltip } =
    getVscReportActionState(vscReport);

  return (
    <TableCard onClick={() => onView?.(vscReport.id)}>
      <div className="data-card-header">
        <div className="flex items-center gap-2">
          {isSelectable && (
            <DataTableRowCheckbox checked={isSelected} onCheckedChange={() => onToggleSelect?.()} />
          )}
          <LabelBadge
            label={statusConfig.label}
            icon={StatusIcon}
            variant={statusConfig.badgeVariant}
            color={statusConfig.badgeColor}
            size="sm"
          />
        </div>
      </div>

      <div className="data-card-content">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground text-sm">{sessionDisplayText}</span>
            {establishmentName && (
              <span className="text-xs text-muted-foreground">{establishmentName}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <UserCheck className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="text-sm text-foreground">{volunteerName}</span>
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(vscReport.id);
            }}
          />
          {canEdit && (
            <Button
              preset="row-edit"
              disabled={editDisabled}
              tooltipText={editTooltip}
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
            />
          )}
          {canValidate && (
            <Button
              preset="row-validate"
              disabled={validateDisabled}
              tooltipText={validateTooltip}
              onClick={(e) => {
                e.stopPropagation();
                onValidate?.();
              }}
            />
          )}
          {canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
